import type { ScheduleItem } from '../types/index.js'

/**
 * Reconnaissance d'un décrochage de direct en cours, à partir de la seule grille
 * du jour déjà présente dans le store.
 *
 * Signature d'un décrochage : l'antenne porte un élément de comblage (pub,
 * jingle, infoservice, interlude) dont la suite contiguë **commence exactement
 * là où un segment de direct s'est terminé**. C'est ce raccord à la seconde qui
 * distingue un décrochage d'un simple enchaînement de grille : un comblage
 * ordinaire suit un programme, pas un direct tronqué.
 *
 * Module pur : aucune requête, aucun état. L'interface s'en sert pour proposer
 * « Raccrocher », le serveur reste seul juge de ce qui est réellement écrit.
 */

/** Types à heure fixe : ils bornent la suite de comblage. */
const ANCHOR_TYPES = ['PROGRAM', 'LIVE']

/** Tolérance d'adjacence entre deux éléments d'une même suite, en ms. */
const ADJACENCY_TOLERANCE_MS = 1_500

export interface LiveBreakState {
  /** Vrai quand l'antenne est en décrochage d'un bloc de direct. */
  breaking: boolean
  /** Segment de direct qui reprendra l'antenne, s'il est déjà posé. */
  resumeItem: ScheduleItem | null
  /** Fin de la suite de comblage : la borne jusqu'à laquelle le direct revient. */
  runEndMs: number | null
  /** Direct dont on a décroché, tel qu'il figure en grille. */
  brokenFrom: ScheduleItem | null
}

const IDLE: LiveBreakState = { breaking: false, resumeItem: null, runEndMs: null, brokenFrom: null }

function ms(value: string): number {
  return new Date(value).getTime()
}

type LiveBlockRef = Pick<ScheduleItem, 'bundleId' | 'programId' | 'liveSourceId'>

/**
 * Miroir de `belongsToSameLiveBlock` (`backend/src/lib/live-break-plan.ts`) :
 * le serveur ne raccroche qu'un segment du bloc décroché. Même `bundleId` pour
 * les blocs récents ; même fiche + même source pour les anciens, dont au moins
 * un identifiant doit exister.
 */
export function belongsToSameLiveBlock(previous: LiveBlockRef, candidate: LiveBlockRef): boolean {
  const previousBundle = previous.bundleId ?? null
  const candidateBundle = candidate.bundleId ?? null
  if (previousBundle || candidateBundle) {
    return previousBundle !== null && candidateBundle === previousBundle
  }
  const previousProgram = previous.programId ?? null
  const previousSource = previous.liveSourceId ?? null
  const hasLegacyIdentity = previousProgram !== null || previousSource !== null
  return hasLegacyIdentity
    && (candidate.programId ?? null) === previousProgram
    && (candidate.liveSourceId ?? null) === previousSource
}

export function resolveLiveBreakState(
  items: ScheduleItem[],
  onAirItem: ScheduleItem | null,
): LiveBreakState {
  if (!onAirItem || onAirItem.itemType === 'LIVE') return IDLE

  const sorted = [...items].sort((a, b) => ms(a.startTime) - ms(b.startTime))
  const onAirIndex = sorted.findIndex(item => item.id === onAirItem.id)
  if (onAirIndex < 0) return IDLE

  // Remonte au début de la suite de comblage qui porte l'antenne.
  let firstIndex = onAirIndex
  while (firstIndex > 0) {
    const previous = sorted[firstIndex - 1]
    if (ANCHOR_TYPES.includes(previous.itemType)) break
    if (ms(sorted[firstIndex].startTime) - ms(previous.endTime) > ADJACENCY_TOLERANCE_MS) break
    firstIndex--
  }

  const runStartMs = ms(sorted[firstIndex].startTime)
  const candidates = sorted.filter(item => item.itemType === 'LIVE'
    && Math.abs(ms(item.endTime) - runStartMs) <= ADJACENCY_TOLERANCE_MS)
  const brokenFrom = candidates.length > 0 ? candidates[candidates.length - 1] : null
  if (!brokenFrom) return IDLE

  // Fin de la suite : on avance tant que les éléments se touchent.
  let runEndMs = ms(onAirItem.endTime)
  let resumeItem: ScheduleItem | null = null
  for (const item of sorted.slice(onAirIndex + 1)) {
    if (ms(item.startTime) - runEndMs > ADJACENCY_TOLERANCE_MS) break
    if (ANCHOR_TYPES.includes(item.itemType)) {
      // Une reprise de direct : c'est elle qu'on avancera — à condition qu'elle
      // appartienne au bloc décroché. Le direct suivant de la grille (autre
      // émission, même studio) n'est pas une reprise : l'envoyer comme `itemId`
      // fait refuser chaque « Reprendre le direct » par le serveur.
      if (item.itemType === 'LIVE' && belongsToSameLiveBlock(brokenFrom, item)) resumeItem = item
      break
    }
    runEndMs = ms(item.endTime)
  }

  return { breaking: true, resumeItem, runEndMs, brokenFrom }
}

/** Il doit rester au moins ce temps au direct pris — miroir de `LIVE_BREAK_MIN_RESUME_SEC`. */
export const WAITING_LIVE_MIN_REMAINING_MS = 10_000

/**
 * Directs d'un AUTRE bloc encore en attente pendant un décrochage, triés par
 * heure. Ce sont eux que « Prendre … » peut faire passer à l'antenne une fois
 * leur heure de grille dépassée (`isWaitingLiveDue`) — miroir d'affichage de
 * `checkWaitingLiveTake` (`backend/src/lib/live-break-plan.ts`), le serveur
 * reste seul juge.
 */
export function listWaitingLives(items: ScheduleItem[], brokenFrom: ScheduleItem | null): ScheduleItem[] {
  if (!brokenFrom) return []
  return items
    .filter(item => item.itemType === 'LIVE'
      && (item.status === 'SCHEDULED' || item.status === 'LOADING')
      && !belongsToSameLiveBlock(brokenFrom, item))
    .sort((a, b) => ms(a.startTime) - ms(b.startTime))
}

/** Le direct en attente a passé son heure de grille et il lui reste du temps. */
export function isWaitingLiveDue(item: ScheduleItem, nowMs: number): boolean {
  return ms(item.startTime) <= nowMs && ms(item.endTime) - nowMs >= WAITING_LIVE_MIN_REMAINING_MS
}

/**
 * Le direct en attente peut-il être pris en avance, avant son heure de grille ?
 * Seulement s'il est la prochaine ancre : aucun programme ni autre direct encore
 * à venir entre l'élément à l'antenne et lui. Sinon, le prendre passerait
 * par-dessus une émission programmée — le serveur le refuserait de toute façon.
 */
export function canTakeWaitingLiveEarly(
  items: ScheduleItem[],
  waitingLive: ScheduleItem,
  onAirItem: ScheduleItem,
  nowMs: number,
): boolean {
  if (ms(waitingLive.startTime) <= nowMs) return false
  if (ms(waitingLive.endTime) - nowMs < WAITING_LIVE_MIN_REMAINING_MS) return false
  const fromMs = ms(onAirItem.startTime)
  const untilMs = ms(waitingLive.startTime)
  return !items.some(item => item.id !== waitingLive.id
    && ANCHOR_TYPES.includes(item.itemType)
    && (item.status === 'SCHEDULED' || item.status === 'LOADING')
    && ms(item.startTime) >= fromMs
    && ms(item.startTime) < untilMs)
}
